import { AxiosError } from 'axios'
import {
  createContext,
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useContext,
} from 'react'

import { ErrorMessages } from '@lib/helpers'
import { LanguageContext } from '@lib/language'
import { PrintShopFormContext } from './print-shop-form-context'
import { PrintShopFormStringsContext } from './print-shop-form-strings-context'
import { maximumPrintFileSize, uploadShopPrints } from './request'
import { getOrCreateShop } from './shop'
import { PrintFile, ShopPrint } from './types'

interface PrintShopFormUploadContextProps {
  uploadQueue: PrintFile[]
  uploadErrors: ErrorMessages
  addToUploadQueue: (file: PrintFile) => void
}

interface PrintShopFormUploadContextProviderProps {
  children?: ReactNode
}

export const PrintShopFormUploadContext =
  createContext<PrintShopFormUploadContextProps>({
    uploadQueue: [],
    uploadErrors: {},
    addToUploadQueue: () => {},
  })

/**
 * The shop form upload context provider.
 */
export const PrintShopFormUploadContextProvider = ({
  children,
}: PrintShopFormUploadContextProviderProps) => {
  const { locale } = useContext(LanguageContext)
  const { shopState, dispatchShopState } = useContext(PrintShopFormContext)
  const { shopFormStrings } = useContext(PrintShopFormStringsContext)

  const [uploadErrors, setUploadErrors] = useState<ErrorMessages>({})
  const [uploadQueue, setUploadQueue] = useState<PrintFile[]>([])
  const [currentUploadItem, setCurrentUploadItem] = useState<PrintFile | null>(
    null,
  )

  const addToUploadQueue = useCallback((file: PrintFile) => {
    setUploadQueue((currentQueue) => [...currentQueue, file])
  }, [])

  useEffect(() => {
    // Upload files one at a time
    if (currentUploadItem || uploadQueue.length === 0) {
      return
    }

    const processFile = async () => {
      let uploadItem = uploadQueue[0]
      const uploadAreaId = uploadItem.uploadAreaId

      // Initiate upload
      setCurrentUploadItem(uploadItem)

      // Remove error
      setUploadErrors((currentUploadErrors) => {
        return {
          ...currentUploadErrors,
          [uploadAreaId]: '',
        }
      })

      try {
        // Check if shop ID isn't set
        let shopId = uploadItem.shopId

        if (!shopId) {
          const newShop = await getOrCreateShop(
            locale,
            shopState,
            dispatchShopState,
          )
          shopId = newShop.id
        }

        if (!shopId) {
          throw new Error('Shop ID not found - upload aborted')
        }

        // Add values and image file to form data
        const formData = new FormData()
        formData.append('shopId', shopId)
        formData.append('printSlotType', uploadItem.printSlotType)
        formData.append('printTypes', uploadItem.printTypes.join(','))

        if (uploadItem.printColor) {
          formData.append('printColor', uploadItem.printColor)
        }

        if (uploadItem.isOptimized) {
          formData.append('isOptimized', 'yes')
        }

        formData.append('file', uploadItem.file)

        const newShopPrints = await uploadShopPrints(
          uploadItem.locale,
          formData,
        )

        // Add successfully uploaded prints to the list
        newShopPrints.forEach((shopPrint) => {
          dispatchShopState({
            action: 'addPrint',
            shopPrint,
          })
        })
      } catch (error) {
        console.error(error)

        // Set upload error
        const isFileSizeError = (error as AxiosError).response?.status == 413
        const maximumFileSize = Math.round(maximumPrintFileSize / 1024 / 1024)

        setUploadErrors((currentUploadErrors) => {
          return {
            ...currentUploadErrors,
            [uploadAreaId]: isFileSizeError
              ? shopFormStrings.shopFormUploadPrintSizeError?.replace(
                  /{maximum_size}/gi,
                  `${maximumFileSize}MB`,
                )
              : shopFormStrings.shopFormUploadPrintError,
          }
        })
      }

      // Remove file from the queue
      setUploadQueue((currentUploadQueue) => {
        return currentUploadQueue.filter((item) => item.id !== uploadItem.id)
      })
      setCurrentUploadItem(null)
    }
    processFile()
  }, [currentUploadItem, dispatchShopState, shopFormStrings, uploadQueue])

  return (
    <PrintShopFormUploadContext.Provider
      value={{
        uploadQueue,
        uploadErrors,
        addToUploadQueue,
      }}
    >
      {children}
    </PrintShopFormUploadContext.Provider>
  )
}
